import type Route from '@ember/routing/route';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import { AiTrackingOrigin } from '@databox/types/domains/ai/chat.enums';

import type {
	APIData,
	BoardPreviewMap,
	CreateRecordProperties,
	PaginationDirection,
	RecordData,
	RequestError,
	Service,
	SourcesMap,
	TemplateResolve,
} from 'webapp-types';
import { showTemplateCreationResult } from 'webapp/const/template-resolve';
import { BillingFeature, ModelName, PopupEvent, TemplateObjectType } from 'webapp/enums/enums';
import { NotificationCreateType } from 'webapp/enums/notifications';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getPreviousOrNextItemByDirection } from 'webapp/utils/common';

import BoardLoopsPopupEditComponent from 'webapp/components/board-loops/popup-edit';
import type DesignerController from 'webapp/controllers/designer';
import DesignerBoardIndexRoute from 'webapp/routes/designer/board';

import type Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import type Template from 'webapp/models-custom/template';
import type BoardLoopRecord from 'webapp/models/board-loop';

import { Severity } from 'webapp/services/logger';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { TagCountList } from 'webapp/services/model-manager/tags';
import type { TrackingOriginForTemplateUsed } from 'webapp/services/model-manager/templates';

export type TrackingOriginType =
	| 'home'
	| 'loops'
	| 'page'
	| 'designer'
	| 'designer_bar'
	| 'share popup'
	| 'notifications'
	| `${AiTrackingOrigin}_AIchat`
	| AiTrackingOrigin.GENIE;

export type TrackingOriginTypeForPreview = 'page' | 'home' | 'loops' | 'sidebar' | 'notifications';
export type TrackingOriginTypeForDuplicate = 'home' | 'loops';
export type TrackingOriginForTemplateCreated =
	'home' | 'board-loops' | 'editor' | 'account_templates';

export default class ModelManagerBoardLoopsService extends ModelManagerForStoreService<
	BoardLoopRecord,
	BoardLoop
> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('logger') declare loggerService: Service.Logger;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('navigation-main') declare navigationMainService: Service.NavigationMain;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	private _boardLoopModels: BoardLoop[] = [];

	@cached
	get boardLoopModels(): BoardLoop[] {
		this.updateModelCache(this._boardLoopModels, this.savedRecords);
		return [...this._boardLoopModels];
	}

	@cached
	get normalBoardLoopModels(): BoardLoop[] {
		return this.normalBoardLoopModelsWithDeleted.filter(
			(boardLoop: BoardLoop) => !boardLoop.deleted,
		);
	}

	@cached
	get normalBoardLoopModelsWithDeleted(): BoardLoop[] {
		// also includes public templates in the public template space
		return this.boardLoopModels.filter((boardLoop: BoardLoop) => {
			const template = boardLoop.templateModel;

			return (
				!boardLoop.templateId ||
				(this.accountService.isPublictemplatesSpace &&
					template?.isPublic &&
					template.objectType === TemplateObjectType.BOARD_LOOP)
			);
		});
	}

	@cached
	get accountTemplateBoardLoopModels(): BoardLoop[] {
		return this.boardLoopModels.filter((boardLoop: BoardLoop) => {
			const template = boardLoop.templateModel;

			return (
				!!template &&
				template.objectType === TemplateObjectType.BOARD_LOOP &&
				!template.isPublic
			);
		});
	}

	get assignedTags(): TagCountList[] {
		const tags = this.savedRecords.reduce((acc: number[], record: BoardLoopRecord) => {
			acc.push(...record.tags);
			return acc;
		}, []);
		return this.mmTagsService.getTagCountList(tags);
	}

	findMostAppropriateLoopForBoard(board: Board): BoardLoop | null {
		// TODO: more "smart", find closest to start of loop, etc.
		return (
			this.boardLoopModels.find((boardLoop) => {
				return boardLoop.canCurrentUserEdit && boardLoop.isBoardIncluded(board);
			}) || null
		);
	}

	startCreateNewFlow(origin?: TrackingOriginType, board?: Board): Promise<BoardLoop> {
		let items = [...this.mmBoardsService.normalBoardModels],
			maxSelectable = BoardLoop.maxBoardsInLoop;

		if (board) {
			items = items.filter((item) => board.id !== item.id);
			maxSelectable--;
		}

		return new Promise((resolve, reject) => {
			const popup = this.mmBoardsService.createBoardsSelectListModal();

			popup.open({
				model: items,
				maxSelectable,
				onCancel: reject,
				onConfirm: (selected: number[]) => {
					let boardIds = selected;

					if (board) {
						boardIds = [board.numericId!, ...selected];
					}

					const boardLoop = this.createNewModel({
						title: 'New loop',
						ownerId: this.currentUserService.numericId,
						boardIds,
					});

					this.openEditPopup(boardLoop, origin, () => {
						if (boardLoop && !boardLoop.isNew) {
							resolve(boardLoop);
						} else {
							reject();
						}
					});

					return Promise.resolve(true);
				},
			});
		});
	}

	openEditPopup(
		boardLoop: BoardLoop,
		trackingOrigin: TrackingOriginType = 'page',
		onClose?: () => void,
	): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const origin = trackingOrigin === 'page' ? 'loops' : trackingOrigin;

		this.analyticsService.event(TrackingEvent.LOOP_EDIT_POPUP_OPEN, {
			origin,
			isNew: boardLoop.isNew,
		});

		const popupBoardLoopsEdit = this.popupService.createModal(BoardLoopsPopupEditComponent, {
			popupClass: 'popup-type-board-loop-edit',
			allowStacked: true, // used because of: boards/popup-select-list
		});

		popupBoardLoopsEdit.open({
			model: this.createModel(boardLoop.model), // a copy
			trackingOrigin: origin,
		});

		if (onClose) {
			popupBoardLoopsEdit.on(PopupEvent.CLOSING, this, onClose);
		}
	}

	toggleFavorite(boardLoop: BoardLoop): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const layer = this.infolayerService.loading('Saving...');

		boardLoop.favorite = !boardLoop.favorite;
		if (boardLoop.favorite) {
			this.analyticsService.event(TrackingEvent.LOOP_FAVORITED, {
				name: boardLoop.name,
				datasources: boardLoop.specificSourceTypesKeys,
			});
		}
		boardLoop
			.save()
			.then(
				() => {
					layer.ok(
						`Looped dashboard <strong>${boardLoop.name}</strong> ${boardLoop.favorite ? '' : 'un'}favorited${boardLoop.favorite ? ' and moved to the top' : ''}!`,
					);
				},
				(errorData: RequestError) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	createAndOpenNewNotificationForModel(model: BoardLoop, trackingOrigin: TrackingOriginType) {
		this.mmNotificationsService.createNewForFeature(
			NotificationCreateType.BOARD,
			model,
			trackingOrigin,
		);
	}

	openSharePopup(
		boardLoop: BoardLoop,
		trackingOrigin: TrackingOriginType = 'page',
		previewMap: BoardPreviewMap = {},
	): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const origin = trackingOrigin === 'page' ? 'loops' : trackingOrigin;

		this.analyticsService.event(TrackingEvent.DATABOARD_SHARE_POPUP_OPEN, {
			origin,
			type: 'loop',
		});

		this.mmBoardsService.openSharePopup({
			model: this.createModel(boardLoop.model), // a copy
			trackingOrigin: origin,
			previewHash: previewMap,
		});
	}

	openPreviewPopup(
		loop: BoardLoop,
		origin: TrackingOriginTypeForPreview = 'page',
		boardLoops: BoardLoop[] = [],
		onPopupClose?: () => void,
	): void {
		if (origin !== null) {
			this.analyticsService.event(TrackingEvent.DATABOARD_SHOW_PREVIEW, {
				loop_id: loop.numericId,
				name: loop.name,
				datasources: loop.specificSourceTypesKeys,
				origin,
			});
		}

		let beforeOpen: Promise<unknown>;
		if (this.mmBoardsService.isBoardPreviewPopupOpen()) {
			// if already open, close the previous and open a new one
			beforeOpen = this.popupService.activePopup?.close() || Promise.resolve();
		} else {
			beforeOpen = Promise.resolve();
		}

		void beforeOpen.then(() => {
			const popup = this.mmBoardsService.createAndOpenPreviewPopup({
				loop,
				isSwitcherShown: boardLoops?.length > 1,
				trackingOrigin: origin,
				onSwitchBoard: (currentBoardLoop, direction: PaginationDirection) => {
					return getPreviousOrNextItemByDirection(
						boardLoops,
						currentBoardLoop as BoardLoop,
						direction,
					);
				},
			});

			if (onPopupClose) {
				popup.on(PopupEvent.CLOSING, this, () => {
					onPopupClose();
				});
			}
		});
	}

	openFullscreenView(boardLoop: BoardLoop): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		window.open(boardLoop.internalUrl);
	}

	openInDesigner(boardLoop: BoardLoop): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (boardLoop.canCurrentUserEditSomeBoard) {
			this.navigationService
				.transitionToRoute('designer.board', boardLoop.boardsEditableByUser[0]!.model)
				.then((route: Route /*  | TransitionState */) => {
					let designerController;

					if (route instanceof DesignerBoardIndexRoute) {
						designerController = route.controllerFor('designer');
					} else {
						// in case we staying on same route, transition state is returned instead of route
						designerController =
							this.navigationService.appRoute.controllerFor('designer');
					}

					(designerController as DesignerController).openLoop(boardLoop);
				});
		} else {
			this.infolayerService.error(
				"You don't have permission to edit any of the dashboards in this loop.",
			);
		}
	}

	openAccountTemplateEditPopup(
		model: BoardLoop | null,
		trackingOrigin: TrackingOriginForTemplateCreated,
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}
		const modelCopy = model ? this.createModelFromRecord(model.model) : null;
		this.mmTemplatesService.openAccountTemplateEditPopup(
			TemplateObjectType.BOARD_LOOP,
			modelCopy,
			trackingOrigin,
		);
	}

	openPublicTemplateEditPopup(model: BoardLoop): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		// make sure templates are loaded
		void this.mmTemplatesService.requestBoardPublicTemplates().then(() => {
			this.mmTemplatesService.openPublicTemplateEditPopup(model);
		});
	}

	openScheduleSnapshotModal(
		boardLoop: BoardLoop,
		trackingOrigin: TrackingOriginType = 'page',
	): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		this.mmBoardSharesService.createAndEditForLoop(boardLoop, trackingOrigin);
	}

	checkIfCanUseTemplate(template: Template, openWarning = false): boolean {
		let actionPassesLimitations = false;

		if (this.billingService.checkIfCan(BillingFeature.BOARDS, openWarning)) {
			if (
				template.objectType === TemplateObjectType.BOARD_LOOP &&
				this.billingService.checkIfCan(BillingFeature.BOARD_LOOPS, openWarning)
			) {
				if (
					template.isPublic ||
					this.billingService.checkIfCan(BillingFeature.ACCOUNT_TEMPLATES, openWarning)
				) {
					actionPassesLimitations = true;
				}
			}
		}

		return actionPassesLimitations;
	}

	requestCreateBoardLoopFromTemplate(
		templateModel: Template,
		data: TemplateResolve.NormalizedResolvePayload,
		trackOrigin = '',
	) {
		const fromId = templateModel.numericId!,
			layer = this.infolayerService.loading('Building looped dashboard...');

		return this.mmTemplatesService
			.useTemplate(templateModel, data, trackOrigin as TrackingOriginForTemplateUsed)
			.then(
				(reply) => {
					const boardLoopData = (reply as APIData.Templates.UseBoardLoopTemplateResponse)
						.boardLoops?.[0];
					const boardsData = reply.boards || [];
					if (boardLoopData) {
						const boardLoopModel = this.normalBoardLoopModels.find(
							(item) => item.id === String(boardLoopData.id),
						);

						showTemplateCreationResult(layer, reply, 'Looped dashboard');

						this.analyticsService.event(TrackingEvent.LOOP_CREATED, {
							origin: trackOrigin,
						});

						boardsData.forEach((boardData) => {
							const boardModel = this.mmBoardsService.normalBoardModels.find(
								(item) => item.id === String(boardData.id),
							);

							if (boardModel) {
								this.mmBoardsService.triggerDataboardCreated(
									{ from: trackOrigin },
									boardModel,
								);
							}
						});

						if (boardLoopModel) {
							const boardLoopRecord = boardLoopModel.model;

							boardLoopRecord.newBadge = true;

							return boardLoopRecord;
						}
					} else {
						layer.error('Error building looped dashboard');
						this.loggerService.logError(
							'[bgEvent] create board loop',
							{
								fromId,
								response: JSON.stringify(reply),
							},
							Severity.INFO,
						);
					}

					return null;
				},
				(errorData) => {
					layer.close();

					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	createBoardLoopFromTemplate(
		templateModel: Template,
		trackOrigin = '',
		afterCreated?: (boardLoop: BoardLoopRecord) => void,
		onResolveModalClose?: () => void,
		sourcesMap: SourcesMap = {},
		skipResolvePopup = false,
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const onAllResolved = (data?: TemplateResolve.NormalizedResolvePayload) => {
			const payload = data ?? { dataSourceTypeMap: {} };

			void this.requestCreateBoardLoopFromTemplate(templateModel, payload, trackOrigin).then(
				(boardLoopRecord) => {
					if (boardLoopRecord && afterCreated) {
						afterCreated(boardLoopRecord);
					}
				},
			);
		};

		if (this.checkIfCanUseTemplate(templateModel, true)) {
			if (skipResolvePopup || !templateModel.sources.length) {
				onAllResolved({ dataSourceTypeMap: sourcesMap });
			} else {
				this.mmTemplatesService.openResolvePopup(
					templateModel,
					onAllResolved,
					trackOrigin,
					onResolveModalClose,
				);
			}
		}
	}

	createNewWithTracking() {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.LOOP_NEW);
		this.startCreateNewFlow().catch(() => {
			/* skip */
		});
	}

	afterCreateBoardLoopFromTemplate(boardLoopRecord: BoardLoopRecord) {
		const afterTransition = () => {
			const boardLoopModel = this.createModel(boardLoopRecord);
			this.openPreviewPopup(boardLoopModel);
		};

		this.navigationService.transitionToRoute('boards.loops.index').then(afterTransition);
	}

	downloadSnapshotWithInfolayer(loop: BoardLoop) {
		const infolayer = this.infolayerService.loopPdf(loop);

		return this.requestService
			.api<APIData.Snapshot.Response>('POST', `/board-loops/${loop.id}/snapshoot`, null, {
				timeout: 5 * 60 * 1000,
			})
			.then((response) => {
				infolayer.setDone(response);
			})
			.catch((e: RequestError) => {
				infolayer.setError('Error generating snapshot');
				this.requestService.handleGenericThrownError(e);
			});
	}

	duplicate(
		boardLoop: BoardLoop,
		trackingOrigin: TrackingOriginTypeForDuplicate | null = null,
	): Promise<BoardLoopRecord> {
		if (this.currentUserService.handleDemo()) {
			return Promise.reject();
		}

		const boardLoopCopy = this.createModelCopy(boardLoop);

		const infolayer = this.infolayerService.loading('Copying looped dashboard...');

		return new Promise((resolve, reject) => {
			boardLoopCopy
				.save()
				.then(
					(copiedBoardLoopRecord: BoardLoopRecord) => {
						this.analyticsService.event(TrackingEvent.LOOP_DUPLICATE, {
							origin: trackingOrigin,
						});
						infolayer.ok('Looped dashboard copied!');
						resolve(copiedBoardLoopRecord);
					},
					(errorData) => {
						infolayer.close();
						throw errorData;
					},
				)
				.catch((err: RequestError) => {
					this.requestService.handleGenericThrownError(err);
					reject(err);
				});
		});
	}

	delete(boardLoop: BoardLoop, cb?: () => number, permanently = false): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const infolayer = this.infolayerService.loading('Deleting looped dashboard...');
			boardLoop
				.delete()
				.then(
					() => {
						let waitBeforeRemove = 0;

						if (typeof cb === 'function') {
							waitBeforeRemove = cb();
						}

						later(() => {
							infolayer.ok('Looped dashboard deleted!');
						}, waitBeforeRemove);
					},
					(errorData) => {
						infolayer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Are you sure you want to delete the looped dashboard "${boardLoop.title}" ${permanently ? 'permanently' : ''}?`,
			text: permanently
				? '<strong>This cannot be undone.</strong>'
				: `"<strong>${boardLoop.title}" will be moved to Recently deleted and deleted permanently after 30 days.</strong><br><br>The dashboards included in the loop will not be deleted.`,
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	restore(model: BoardLoop) {
		if (!this.billingService.checkIfCan(BillingFeature.BOARD_LOOPS, true)) {
			return;
		}

		return this.requestService
			.api<APIData.BoardLoops.Restore.Response>('POST', '/board-loops/restore', {
				boardLoopId: model.id,
			})
			.then((response) => {
				this.storeService.pushPayload(response);
				this.infolayerService.ok(
					`<strong>${model.name}</strong> loop successfully restored.`,
				);
				this.analyticsService.event(TrackingEvent.LOOP_CREATED, {
					origin: 'recently_deleted_restore',
				});
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	// ------ Requests ------

	_boardLoopsRequested = false;
	_boardLoopsRequestPromise: Promise<void> | null = null;
	requestBoardLoops(): Promise<void> {
		const variablePrefix = 'boardLoops';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.storeFindAll(ModelName.BOARD_LOOP);
			},
		);
	}

	requestActivityLogBoardLoops(params?: {
		preStoreFn?: () => Promise<void>;
		queryParams?: Record<string, unknown>;
	}): Promise<RecordData.Payload> {
		return this.requestService.api<RecordData.Payload>(
			'GET',
			'/board-loops',
			params?.queryParams,
		);
	}

	@tracked isRecentlyDeletedLoading = false;
	requestDeletedBoardLoops() {
		return this.requestService
			.api<APIData.BoardLoops.RecentlyDeleted.Response>('GET', '/board-loops', {
				onlyDeleted: true,
			})
			.then((response) => {
				this.storeService.pushPayload(response);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService))
			.finally(() => {
				this.isRecentlyDeletedLoading = false;
			});
	}

	// ------ Batch actions ------

	override get confirmWord(): string | undefined {
		return;
	}

	override batchFavorite(items: BoardLoop[]) {
		return super.batchFavorite(items);
	}

	override batchDelete(items: BoardLoop[]) {
		return super.batchDelete(items).then(() => {
			this.flowPromotionsService.startRecentlyDeletedTourForBoardLoops();
		});
	}

	batchRestore(items: BoardLoop[]) {
		return super.batchRestoreAction(items).then(() => {
			items.forEach((item) => {
				item.deleted = null;
			});
		});
	}

	// ------ For-store ------

	modelName = ModelName.BOARD_LOOP;
	override modelTitle = 'looped dashboard';

	createModel(model: BoardLoopRecord): BoardLoop {
		return new BoardLoop({ model });
	}

	override createNewModel(properties: CreateRecordProperties<BoardLoopRecord> = {}): BoardLoop {
		if (typeof properties.ownerId === 'undefined') {
			properties.ownerId = this.currentUserService.numericId;
		}
		if (typeof properties.title === 'undefined') {
			properties.title = 'My dashboard presentation';
		}
		return super.createNewModel(properties);
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/board-loops': ModelManagerBoardLoopsService;
	}
}
